import {
  QueryBuilderEntityConfigType,
  QueryBuilderEntityValueOptionType,
  QueryBuilderInputContextType,
  QUERY_BUILDER_KEYWORD_ENTITY_KEY,
  QueryBuilderTypingCommitActionType,
} from '../../../../types';
import { filterEntityOptionsBySearch } from '../filterEntityOptionsBySearch';
import { filterOperatorsBySearch } from '../filterOperatorsBySearch';
import { resolveBracketInsertFromSearch } from '../resolveBracketInsertFromSearch';

type ResolveTypingCommitArgsType = {
  context: QueryBuilderInputContextType;
  search: string;
  entities: QueryBuilderEntityConfigType[];
  valueOptions: QueryBuilderEntityValueOptionType[];
  canOpenBracket: boolean;
  canCloseBracket: boolean;
  highlightedIndex?: number;
};

export const resolveTypingCommit = ({
  context,
  search,
  entities,
  valueOptions,
  canOpenBracket,
  canCloseBracket,
  highlightedIndex = 0,
}: ResolveTypingCommitArgsType): QueryBuilderTypingCommitActionType | null => {
  const trimmedSearch = search.trim();

  if (!trimmedSearch) {
    return null;
  }

  const bracketInsert = resolveBracketInsertFromSearch(
    context,
    trimmedSearch,
    canOpenBracket,
    canCloseBracket
  );

  if (bracketInsert === 'open') {
    return { type: 'openBracket' };
  }

  if (bracketInsert === 'close') {
    return { type: 'closeBracket' };
  }

  if (context === 'expectOperand') {
    const filteredEntities = filterEntityOptionsBySearch(entities, trimmedSearch);

    if (filteredEntities.length === 0) {
      return {
        type: 'entityValue',
        entityKey: QUERY_BUILDER_KEYWORD_ENTITY_KEY,
        value: trimmedSearch,
      };
    }

    const highlightedEntity =
      filteredEntities[highlightedIndex] ?? filteredEntities[0];

    return {
      type: 'entity',
      entityKey: highlightedEntity.key,
    };
  }

  if (context === 'expectValue') {
    const normalizedSearch = trimmedSearch.toLowerCase();
    const matchingOptions = valueOptions.filter((option) =>
      option.label.toLowerCase().includes(normalizedSearch)
    );

    if (matchingOptions.length === 0) {
      return {
        type: 'value',
        value: trimmedSearch,
      };
    }

    const highlightedOption =
      matchingOptions[highlightedIndex] ?? matchingOptions[0];

    return {
      type: 'value',
      value: highlightedOption.label,
    };
  }

  if (context === 'expectOperatorOrClose') {
    const filteredOperators = filterOperatorsBySearch(trimmedSearch);

    if (filteredOperators.length === 0) {
      return null;
    }

    const highlightedOperator =
      filteredOperators[highlightedIndex] ?? filteredOperators[0];

    return {
      type: 'operator',
      operator: highlightedOperator,
    };
  }

  return null;
};
